import { Container, Typography, Box, Button, Divider, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Snackbar, Alert, CircularProgress, useMediaQuery } from '@mui/material';
import { Edit, Download, Share, ContentCopy, Close, PictureAsPdf } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAliId } from '../hooks/useAliId';
import { useAuthorization } from '../hooks/useAuthorization';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { en } from '../translations/en';
import { getCVData, CVData } from '../utils/cvApi';
import { CvCatalogSelect } from '../components/CvCatalogSelect';
import { BrandIconLink } from '../components/cv/BrandIconLink';
import { mergeBrandEntries } from '../components/cv/mergeBrandEntries';
import { getProfileImageUrl } from '../utils/profileImageApi';
import { getCvPdfUrl, getCvLogoUrl } from '../utils/cvAssetsApi';
import { logger } from '../utils/logger';
import jsPDF from 'jspdf';
import { renderSafeMarkdown } from '../utils/safeMarkdown';

export const CurriculumVitae = () => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useAuth();
  const { aliId } = useAliId();
  const { isCommercialTier } = useAuthorization();
  const { activeCatalogOwnerId, activeDelegation, activeCatalogAliId, ownedCatalogs, delegatedCatalogs } = useActiveCatalog();
  const isCommercial = isCommercialTier();

  // Single source of truth for which delegation is active. Covers the normal
  // path (activeDelegation is set) and the owner-id/ali-id desync state where
  // activeDelegation is null but activeCatalogAliId already holds a delegator's
  // aliId (GL#50 / GH#1096). Both the header and the fetch effect use this so
  // they always agree on which delegator is active.
  const effectiveDelegation = useMemo(
    () =>
      activeDelegation ??
      (!activeCatalogOwnerId && activeCatalogAliId
        ? (delegatedCatalogs.find(d => d.delegatorAliId === activeCatalogAliId) ?? null)
        : null),
    [activeDelegation, activeCatalogOwnerId, activeCatalogAliId, delegatedCatalogs]
  );

  // Resolve the active owned-catalog row when the user has selected one
  // of their own add-ons (only activeCatalogAliId flips for that selection;
  // activeCatalogOwnerId and activeDelegation stay null). Same pattern as
  // MyObjects.tsx so the heading reflects the add-on's persona instead of
  // falling through to the primary's identity.
  const activeOwnedCatalog = useMemo(
    () =>
      !effectiveDelegation && activeCatalogAliId
        ? ownedCatalogs?.find(c => c.catalogAliId === activeCatalogAliId) ?? null
        : null,
    [effectiveDelegation, activeCatalogAliId, ownedCatalogs]
  );
  const showOwnedAddonHeading = activeOwnedCatalog !== null && !activeOwnedCatalog.isPrimary;

  // Three-way display: delegated → active-owned-addon → primary (user) fallback.
  const displayName = effectiveDelegation?.delegatorName
    ?? (showOwnedAddonHeading ? activeOwnedCatalog!.creatorName : null)
    ?? (user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || 'User');

  const displayAliId = effectiveDelegation?.delegatorAliId
    ?? (showOwnedAddonHeading ? activeOwnedCatalog!.catalogAliId : null)
    ?? aliId;
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [cvData, setCvData] = useState<CVData>({});
  const [loading, setLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [cvPdfUrl, setCvPdfUrl] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const brandEntries = useMemo(() => mergeBrandEntries(cvData), [cvData]);
  const hasBiography = !!(cvData.biography && cvData.biography.trim());
  const hasEducation = cvData.education?.some((e) => e.text) ?? false;
  const hasSoloExhibitions = cvData.soloExhibitions?.some((e) => e.text) ?? false;
  const hasGroupExhibitions = cvData.groupExhibitions?.some((e) => e.text) ?? false;
  const hasPublications = cvData.publications?.some((p) => p.text) ?? false;
  const hasAwards = cvData.awards?.some((a) => a.text) ?? false;
  const hasCollections = cvData.collections?.some((c) => c.name) ?? false;
  const hasGalleryRepresentation = cvData.galleryRepresentation?.some((g) => g.name) ?? false;
  const hasAdditionalInfo = !!(cvData.additionalInfo && cvData.additionalInfo.trim());
  const hasAnyContent = hasBiography || hasEducation || hasSoloExhibitions
    || hasGroupExhibitions || hasPublications || hasAwards || hasCollections
    || hasGalleryRepresentation || hasAdditionalInfo || brandEntries.length > 0;

  useEffect(() => {
    const loadCV = async () => {
      try {
        // GL#50 / GH#1096: effectiveDelegation already handles the desync
        // state where activeCatalogOwnerId is null but activeCatalogAliId holds
        // a delegator's aliId. Use it here so the fetch and header share one
        // resolution (no duplicate lookup).
        const ownerId = activeCatalogOwnerId ?? effectiveDelegation?.delegatorUserId ?? undefined;
        // Pass the active owned catalog selection to the CV endpoint so
        // we fetch the right per-catalog row. When delegated (via ownerId),
        // the backend defaults to the delegator's primary CV; catalogAliId
        // is only meaningful for own catalogs (never a delegator's bare aliId).
        const cvCatalogAliId = !ownerId && activeOwnedCatalog ? activeCatalogAliId : undefined;
        const [cvResult, imageResult, pdfResult, logoResult] = await Promise.allSettled([
          getCVData(ownerId, cvCatalogAliId),
          getProfileImageUrl(ownerId, cvCatalogAliId),
          getCvPdfUrl(ownerId, cvCatalogAliId),
          getCvLogoUrl(ownerId, cvCatalogAliId),
        ]);

        if (cvResult.status === 'fulfilled') {
          setCvData(cvResult.value);
        }
        // Setters must run unconditionally so switching owners clears any
        // previously-loaded asset (otherwise stale state from a prior
        // delegator persists when the new owner has none of these assets).
        if (imageResult.status === 'fulfilled') {
          setProfileImageUrl(imageResult.value?.imageUrl ?? null);
        }
        if (pdfResult.status === 'fulfilled') {
          setCvPdfUrl(pdfResult.value?.pdfUrl ?? null);
        }
        if (logoResult.status === 'fulfilled') {
          setLogoUrl(logoResult.value?.logoUrl ?? null);
        }
      } catch (error) {
        logger.error('Failed to load CV', error);
      } finally {
        setLoading(false);
      }
    };
    loadCV();
  }, [activeCatalogOwnerId, activeCatalogAliId, effectiveDelegation, activeOwnedCatalog]);

  const shareUrl = `${window.location.origin}/cv/public/${displayAliId || 'user'}`;

  const handleEdit = () => {
    navigate('/cv/edit');
  };

  const handleDownload = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPos = 20;

    // Helper function to add a section
    const addSection = (title: string, content: string | string[]) => {
      // Check if we need a new page
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }

      // Section title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(title, margin, yPos);
      yPos += 8;

      // Section content
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);

      if (Array.isArray(content)) {
        content.forEach(item => {
          if (item) {
            const lines = doc.splitTextToSize(item, maxWidth);
            lines.forEach((line: string) => {
              if (yPos > 280) {
                doc.addPage();
                yPos = 20;
              }
              doc.text(line, margin, yPos);
              yPos += 5;
            });
          }
        });
      } else if (content) {
        const lines = doc.splitTextToSize(content, maxWidth);
        lines.forEach((line: string) => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, margin, yPos);
          yPos += 5;
        });
      } else {
        doc.setTextColor(150);
        doc.text('No information available', margin, yPos);
        doc.setTextColor(0);
        yPos += 5;
      }

      yPos += 10; // Space after section
    };

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    const userName = displayName;
    doc.text('Curriculum Vitae', margin, yPos);
    yPos += 15;

    // Name and ALI ID
    doc.setFontSize(16);
    doc.text(userName, margin, yPos);
    yPos += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`ALI ID: ${displayAliId || 'N/A'}`, margin, yPos);
    doc.setTextColor(0);
    yPos += 15;

    // Biography
    addSection('Biography', cvData.biography || '');

    // Contact Information
    if (cvData.contact && cvData.contact.length > 0) {
      const contactItems = cvData.contact
        .filter(c => c.type || c.value)
        .map(c => `${c.type}: ${c.value}`);
      addSection('Contact Information', contactItems);
    } else {
      addSection('Contact Information', '');
    }

    // Social Media
    if (cvData.socialMedia && cvData.socialMedia.length > 0) {
      const socialItems = cvData.socialMedia
        .filter(s => s.type || s.link)
        .map(s => `${s.type}: ${s.link}`);
      addSection('Social Media', socialItems);
    }

    // Education
    if (cvData.education && cvData.education.length > 0) {
      const eduItems = cvData.education
        .filter(e => e.years || e.institution || e.degree)
        .map(e => `${e.years ? e.years + ' - ' : ''}${e.institution}${e.degree ? ', ' + e.degree : ''}`);
      addSection('Education', eduItems);
    }

    // Solo Exhibitions
    if (cvData.soloExhibitions && cvData.soloExhibitions.length > 0) {
      const soloItems = cvData.soloExhibitions
        .filter(e => e.year || e.exhibition)
        .map(e => `${e.year ? e.year + ' - ' : ''}${e.exhibition}`);
      addSection('Solo Exhibitions', soloItems);
    }

    // Group Exhibitions
    if (cvData.groupExhibitions && cvData.groupExhibitions.length > 0) {
      const groupItems = cvData.groupExhibitions
        .filter(e => e.year || e.exhibition)
        .map(e => `${e.year ? e.year + ' - ' : ''}${e.exhibition}`);
      addSection('Group Exhibitions', groupItems);
    }

    // Publications
    if (cvData.publications && cvData.publications.length > 0) {
      const pubItems = cvData.publications
        .filter(p => p.year || p.publication)
        .map(p => `${p.year ? p.year + ' - ' : ''}${p.publication}`);
      addSection('Publications', pubItems);
    }

    // Awards & Honors
    if (cvData.awards && cvData.awards.length > 0) {
      const awardItems = cvData.awards
        .filter(a => a.year || a.award || a.organization)
        .map(a => `${a.year ? a.year + ' - ' : ''}${a.award}${a.organization ? ', ' + a.organization : ''}`);
      addSection('Awards & Honors', awardItems);
    }

    // Collections
    if (cvData.collections && cvData.collections.length > 0) {
      const collectionItems = cvData.collections
        .filter(c => c.name)
        .map(c => c.name);
      addSection('Collections', collectionItems);
    }

    // Gallery Representation
    if (cvData.galleryRepresentation && cvData.galleryRepresentation.length > 0) {
      const galleryItems = cvData.galleryRepresentation
        .filter(g => g.name)
        .map(g => g.name);
      addSection('Gallery Representation', galleryItems);
    }

    // Additional Information
    if (cvData.additionalInfo) {
      addSection('Additional Information', cvData.additionalInfo);
    }

    // Save the PDF
    const fileName = `${userName.replace(/\s+/g, '_')}_CV.pdf`;
    doc.save(fileName);
  };

  const handleShare = () => {
    setShareModalOpen(true);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setSnackbarMessage('Link copied to clipboard!');
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{
        py: { xs: 2, md: 4 },
        px: { xs: 2, md: 0 }
      }}>
        {/* Title and buttons outside container */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h1" component="h1" sx={{ color: theme.palette.text.primary }}>
            {isCommercial ? en.profile.commercial.about : en.profile.curriculumVitae}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <CvCatalogSelect />
            {cvPdfUrl && (
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                component="a"
                href={cvPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download CV PDF
              </Button>
            )}
            <Button variant="outlined" startIcon={<Download />} onClick={handleDownload}>
              {cvPdfUrl ? 'Generate PDF' : en.profile.download}
            </Button>
            <Button variant="outlined" startIcon={<Edit />} onClick={handleEdit}>
              {en.profile.edit}
            </Button>
            <Button variant="outlined" startIcon={<Share />} onClick={handleShare}>
              {en.profile.share}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/cv/public/${displayAliId || 'user'}?from=cv`)}
            >
              View as Public
            </Button>
          </Box>
        </Box>

        <Box sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          p: 4,
          boxShadow: theme.shadows[1]
        }}>
          {/* Profile Section */}
          <Box sx={{
            display: 'flex',
            gap: 4,
            mb: 4,
            alignItems: 'flex-start'
          }}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="Artist Logo"
                sx={{
                  width: 120,
                  height: 120,
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            ) : (
              <Avatar
                src={profileImageUrl || undefined}
                sx={{
                  width: 120,
                  height: 120,
                  fontSize: '3rem',
                  bgcolor: theme.palette.primary.main
                }}
              >
                {!profileImageUrl && displayName.split(' ').map(n => n[0] ?? '').join('').slice(0, 2)}
              </Avatar>
            )}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h4" sx={{ color: theme.palette.text.primary }}>
                  {displayName}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                ALI ID: {displayAliId || 'N/A'}
              </Typography>
              {brandEntries.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, ml: -1 }}>
                  {brandEntries.map((entry, idx) => (
                    <BrandIconLink key={`${entry.type}-${idx}`} type={entry.type} value={entry.value} />
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {!hasAnyContent && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Your CV is empty — click Edit to add your biography, experience, and contact info.
              </Typography>
            </Box>
          )}

          {hasBiography && (
            <>
              <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                  {isCommercial ? en.profile.commercial.aboutUs : en.profile.biography}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                  {renderSafeMarkdown(cvData.biography)}
                </Typography>
              </Box>
            </>
          )}

          {!isCommercial && (
            <>
              {hasEducation && (
                <>
                  <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                      {en.profile.education}
                    </Typography>
                    {cvData.education?.map((item, index) =>
                      item.text ? (
                        <Typography key={index} variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                          {renderSafeMarkdown(item.text)}
                        </Typography>
                      ) : null
                    )}
                  </Box>
                </>
              )}

              {hasSoloExhibitions && (
                <>
                  <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                      {en.profile.soloExhibitions}
                    </Typography>
                    {cvData.soloExhibitions?.map((item, index) =>
                      item.text ? (
                        <Typography key={index} variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                          {renderSafeMarkdown(item.text)}
                        </Typography>
                      ) : null
                    )}
                  </Box>
                </>
              )}

              {hasGroupExhibitions && (
                <>
                  <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                      {en.profile.groupExhibitions}
                    </Typography>
                    {cvData.groupExhibitions?.map((item, index) =>
                      item.text ? (
                        <Typography key={index} variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                          {renderSafeMarkdown(item.text)}
                        </Typography>
                      ) : null
                    )}
                  </Box>
                </>
              )}

              {hasPublications && (
                <>
                  <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                      {en.profile.publications}
                    </Typography>
                    {cvData.publications?.map((item, index) =>
                      item.text ? (
                        <Typography key={index} variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                          {renderSafeMarkdown(item.text)}
                        </Typography>
                      ) : null
                    )}
                  </Box>
                </>
              )}

              {hasAwards && (
                <>
                  <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                      {en.profile.awardsHonors}
                    </Typography>
                    {cvData.awards?.map((item, index) =>
                      item.text ? (
                        <Typography key={index} variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                          {renderSafeMarkdown(item.text)}
                        </Typography>
                      ) : null
                    )}
                  </Box>
                </>
              )}

              {hasCollections && (
                <>
                  <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                      {en.profile.collections}
                    </Typography>
                    {cvData.collections?.map((item, index) =>
                      item.name ? (
                        <Typography key={index} variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                          {renderSafeMarkdown(item.name)}
                        </Typography>
                      ) : null
                    )}
                  </Box>
                </>
              )}

              {hasGalleryRepresentation && (
                <>
                  <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                      {en.profile.galleryRepresentation}
                    </Typography>
                    {cvData.galleryRepresentation?.map((item, index) =>
                      item.name ? (
                        <Typography key={index} variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                          {renderSafeMarkdown(item.name)}
                        </Typography>
                      ) : null
                    )}
                  </Box>
                </>
              )}
            </>
          )}

          {hasAdditionalInfo && (
            <>
              <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                  Additional Information
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                  {renderSafeMarkdown(cvData.additionalInfo)}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Share Modal */}
      <Dialog open={shareModalOpen} onClose={() => setShareModalOpen(false)} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          {isCommercial ? 'Share About Page' : 'Share Curriculum Vitae'}
          <IconButton onClick={() => setShareModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Share Link"
            value={shareUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={handleCopyLink} edge="end">
                  <ContentCopy />
                </IconButton>
              )
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};
